import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import RowMenu from '../../components/RowMenu';
import { KINDS } from '../../concepts/kinds';
import { isKnownKind, type ProgramTrack, type ProgramView } from '../../programs/types';
import { AddProgramsMenu } from '../../pages/home/shared';
import { ProgramMenuItems } from '../../pages/home/ClassicHome';
import type { HomeModel } from '../../pages/home/useHome';
import { CATEGORY_GLYPH, colourOfCategory, glyphOf, growthText, motionAllowed, useGrowth } from '../scene/programs';
import { useFollow, useScene, useInsets } from '../scene/useScene';
import type { PopupHandle, SpreadSpec } from './PopupBook';
import { useTight } from '../../components/useTight';

const PER_PAGE = 6;

function useWide() {
  const [wide, setWide] = useState(window.innerWidth > 720);
  useEffect(() => {
    const on = () => setWide(window.innerWidth > 720);
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, []);
  return wide;
}

export default function PopupHome({ h }: { h: HomeModel }) {
  const nav = useNavigate();
  const [track, setTrack] = useState<ProgramTrack>('CURRENT');
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState<string | null>(null);
  const [hovered, setHovered] = useState<string | null>(null);
  const wide = useWide();
  const moving = motionAllowed(h.settings.reduceMotion);
  const all = h.programs.filter((p) => p.track === track);
  const growth = useGrowth(all);

  const chapters = useMemo(() => {
    const order = [...new Set(all.map((p) => p.category ?? 'Other'))].sort((a, b) => {
      const ia = h.categories.indexOf(a), ib = h.categories.indexOf(b);
      return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib) || a.localeCompare(b);
    });
    const spreads: (SpreadSpec & { programs: ProgramView[] })[] = [];
    order.forEach((cat, ci) => {
      const items = all.filter((p) => (p.category ?? 'Other') === cat);
      const parts = Math.ceil(items.length / PER_PAGE);
      for (let n = 0; n < parts; n++) {
        const slice = items.slice(n * PER_PAGE, (n + 1) * PER_PAGE);
        spreads.push({
          key: `${track}:${cat}:${n}`,
          title: cat,
          glyph: CATEGORY_GLYPH[cat] ?? '📘',
          colour: colourOfCategory(cat === 'Other' ? null : cat, ci),
          page: `Chapter ${ci + 1}${parts > 1 ? ` · part ${n + 1}` : ''}`,
          note: `${items.length} ${items.length === 1 ? 'program' : 'programs'} to ${track === 'CURRENT' ? 'practise' : 'keep fresh'}`,
          cards: slice.map((p) => ({ id: p.id, name: p.name, glyph: glyphOf(p), colour: colourOfCategory(p.category, ci) })),
          programs: slice,
        });
      }
    });
    return spreads;
  }, [all.map((p) => `${p.id}:${p.name}:${p.category}`).join(), track, h.categories.join()]);

  const at = Math.min(page, Math.max(0, chapters.length - 1));
  const spread = chapters[at];

  const { canvasRef, handleRef, failed } = useScene<PopupHandle>(
    'popup',
    (canvas, cb) => import('./PopupBook').then(({ mountPopup }) => mountPopup(canvas, moving, cb)),
    { onPick: (id) => setSelected(id), onHover: setHovered },
    (handle) => { handle.setSpreads(chapters, at); handle.setSelected(selected); insets.current(); },
  );
  const insets = useInsets(handleRef, ['.topbar', '.pop-head'], ['.pop-foot']);

  useEffect(() => { handleRef.current?.setSpreads(chapters, 0); setPage(0); setSelected(null); }, [chapters]);
  useEffect(() => { handleRef.current?.turnTo(at); }, [at]);
  useEffect(() => { handleRef.current?.setSelected(selected); }, [selected]);
  useEffect(() => { handleRef.current?.setMoving(moving); }, [moving]);

  const go = (d: number) => {
    setSelected(null);
    setPage((p) => Math.max(0, Math.min(chapters.length - 1, p + d)));
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement;
      if (el.closest('input, select, textarea, [role="menu"], .modal-backdrop')) return;
      if (e.key === 'ArrowRight') go(1);
      if (e.key === 'ArrowLeft') go(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [chapters.length]);

  const cardRef = useRef<HTMLDivElement | null>(null);
  const headRef = useRef<HTMLDivElement | null>(null);
  const tight = useTight(headRef);
  const tipRef = useRef<HTMLDivElement | null>(null);
  useFollow(cardRef, () => (selected ? handleRef.current?.locate(selected) ?? null : null), wide && !!selected && !failed);
  useFollow(tipRef, () => (hovered ? handleRef.current?.locate(hovered) ?? null : null), !!hovered && hovered !== selected && !failed);

  const chosen = spread?.programs.find((p) => p.id === selected) ?? null;
  const tip = spread?.programs.find((p) => p.id === hovered) ?? null;
  const playUrl = (p: ProgramView) => `/play/${p.id}${h.learnerId ? `?learner=${encodeURIComponent(h.learnerId)}` : ''}`;
  const kindLabel = (p: ProgramView) => (isKnownKind(p.kind) ? KINDS[p.kind].label : p.kind);

  return (
    <div className="pop">
      {!failed && <canvas ref={canvasRef} className="scene-canvas" aria-hidden="true" />}

      <div ref={headRef} className={tight ? 'pop-head is-tight' : 'pop-head'}>
        <div className="pop-tabs" role="tablist" aria-label="Which book">
          {(['CURRENT', 'MAINTENANCE'] as const).map((t) => (
            <button
              key={t}
              type="button"
              role="tab"
              aria-selected={track === t}
              className={track === t ? 'pop-tab is-active' : 'pop-tab'}
              onClick={() => { setTrack(t); }}
            >
              {t === 'CURRENT' ? 'Learning now' : 'Learned'}
              <span className="pop-count">{h.programs.filter((p) => p.track === t).length}</span>
            </button>
          ))}
        </div>
        {chapters.length > 1 && (
          <select className="pop-contents" aria-label="Contents" value={at} onChange={(e) => { setSelected(null); setPage(Number(e.target.value)); }}>
            {chapters.map((c, i) => <option key={c.key} value={i}>{c.page} — {c.title}</option>)}
          </select>
        )}
        <div className="pop-head-end">
          <AddProgramsMenu h={h} />
        </div>
      </div>

      {h.error && <div className="error" role="alert">{h.error}</div>}

      {!h.loading && chapters.length === 0 && (
        <div className="pop-empty">
          <h2>{track === 'CURRENT' ? 'This book is empty' : 'Nothing learned yet'}</h2>
          <p>{track === 'CURRENT' ? 'Add a program and it gets its own page.' : 'Programs move into this book once they are learned.'}</p>
        </div>
      )}

      {tip && tip.id !== selected && (
        <div ref={tipRef} className="pop-tip" aria-hidden="true"><span>{tip.name}</span></div>
      )}

      {chosen && (
        <div ref={cardRef} className={wide ? 'pop-card is-floating' : 'pop-card'} role="region" aria-label={chosen.name}>
          <div className="pop-card-inner">
            <p className="pop-card-name">{chosen.name}</p>
            <p className="pop-card-meta">{kindLabel(chosen)} · {growthText(growth.get(chosen.id))}{h.dueIds.has(chosen.id) ? ' · ready for a check' : ''}</p>
            <div className="pop-card-actions">
              <button type="button" className="btn" onClick={() => nav(playUrl(chosen))}>Play</button>
              <RowMenu label={`More for ${chosen.name}`}>
                <ProgramMenuItems h={h} p={chosen} />
              </RowMenu>
              <button type="button" className="btn secondary" onClick={() => setSelected(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {spread && (
        <nav className="pop-foot" aria-label="Pages">
          <button type="button" className="pop-turn" aria-label="Previous page" disabled={at === 0} onClick={() => go(-1)}>‹</button>
          <div className="pop-foot-mid">
            <p className="pop-where"><strong>{spread.title}</strong> · page {at + 1} of {chapters.length}</p>
            <ul className="pop-chips">
              {spread.programs.map((p) => (
                <li key={p.id}>
                  <button
                    type="button"
                    className={selected === p.id ? 'pop-chip is-active' : 'pop-chip'}
                    aria-pressed={selected === p.id}
                    onClick={() => setSelected(p.id)}
                  >
                    <span aria-hidden="true">{glyphOf(p)}</span> {p.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>
          <button type="button" className="pop-turn" aria-label="Next page" disabled={at >= chapters.length - 1} onClick={() => go(1)}>›</button>
        </nav>
      )}
    </div>
  );
}
